import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useReducer,
} from "react";
import { Challenger, Score } from "@/models/pdfData";
import { v4 as uuidv4 } from "uuid";
import { scoreEquals } from "./utils";
import {
  Challenge,
  CompletedChallenge,
  CompletedChallengePart,
  SamsChallengeType,
  StartedChallenge,
  SuccessfulChallenge,
} from "@/models/gameState";

export interface SelectedChallengeValues {
  challenge: ChallengeInProgress;
  setSetScoreBeforeRally: (setScoreBeforeRally: Score) => void;
  setSetScoreBeforeChallenge: (setScoreBeforeChallenge: Score) => void;
  setSetScoreAfterChallenge: (setScoreAfterChallenge: Score) => void;
  setSet: (setNumber: number) => void;
  setTypeOfChallenge: (typeOfChallenge: SamsChallengeType) => void;
  setChallenger: (challenger: Challenger) => void;
  newChallenge: () => void;
  setChallengeResult: (outcome: CompletedChallengePart) => void;
  loadChallenge: (challenge: Challenge) => void;
}

const notInitializedYet = (): any => {
  console.error("GameDataContext has not been initialized yet!");
};
const SelectedChallengeContext = createContext<SelectedChallengeValues>({
  challenge: { id: uuidv4() },
  setSetScoreBeforeRally: notInitializedYet,
  setSetScoreBeforeChallenge: notInitializedYet,
  setSetScoreAfterChallenge: notInitializedYet,
  setSet: notInitializedYet,
  setTypeOfChallenge: notInitializedYet,
  setChallenger: notInitializedYet,
  setChallengeResult: notInitializedYet,
  newChallenge: notInitializedYet,
  loadChallenge: notInitializedYet,
});

export type ChallengeInProgress = Partial<Omit<CompletedChallenge, "id">> & {
  id: string;
};

type ReducerActions =
  | { type: "newChallenge" }
  | { type: "loadChallenge"; challenge: Challenge }
  | {
      type: "setSetScoreBeforeRally";
      setScoreBeforeRally: CompletedChallenge["setScoreBeforeChallenge"];
    }
  | {
      type: "setSetScoreBeforeChallenge";
      setScoreBeforeChallenge: CompletedChallenge["setScoreBeforeChallenge"];
    }
  | {
      type: "setSetScoreAfterChallenge";
      setScoreAfterChallenge: CompletedChallenge["setScoreAfterChallenge"];
    }
  | { type: "setSet"; setNumber: CompletedChallenge["setNumber"] }
  | {
      type: "setTypeOfChallenge";
      challengeType: CompletedChallenge["challengeType"];
    }
  | { type: "setChallenger"; challenger: CompletedChallenge["challenger"] }
  | {
      type: "setChallengeResult";
      challengeResult: CompletedChallengePart;
    };

const reducer: React.Reducer<ChallengeInProgress, ReducerActions> = (
  state,
  action,
): ChallengeInProgress => {
  switch (action.type) {
    case "newChallenge":
      return { id: uuidv4() };
    case "loadChallenge":
      if (!action.challenge.id) {
        action.challenge.id = uuidv4();
      }
      return action.challenge;
    case "setSetScoreBeforeRally":
      return { ...state, setScoreBeforeRally: action.setScoreBeforeRally };
    case "setSetScoreBeforeChallenge":
      return {
        ...state,
        setScoreBeforeChallenge: action.setScoreBeforeChallenge,
      };
    case "setSetScoreAfterChallenge":
      return {
        ...state,
        setScoreAfterChallenge: action.setScoreAfterChallenge,
      };
    case "setSet":
      return { ...state, setNumber: action.setNumber };
    case "setTypeOfChallenge":
      return { ...state, challengeType: action.challengeType };
    case "setChallenger":
      return { ...state, challenger: action.challenger };
    case "setChallengeResult":
      return { ...state, ...action.challengeResult };
    default:
      return state;
  }
};

export const useSelectedChallengeContext = (): SelectedChallengeValues =>
  useContext(SelectedChallengeContext);

const SelectedChallengeContextProvider: FC<PropsWithChildren<{}>> = (props) => {
  const [challenge, dispatch] = useReducer(reducer, { id: uuidv4() });

  const loadChallenge = (challenge: Challenge) => {
    dispatch({ type: "loadChallenge", challenge });
  };

  const setScoreRallyStart: SelectedChallengeValues["setSetScoreBeforeRally"] =
    (scoreRallyStart) => {
      if (!scoreEquals(challenge.setScoreBeforeChallenge, scoreRallyStart)) {
        dispatch({
          type: "setSetScoreBeforeRally",
          setScoreBeforeRally: scoreRallyStart,
        });
      }
    };

  const setScoreBeforeChallenge: SelectedChallengeValues["setSetScoreBeforeChallenge"] =
    (setScoreBeforeChallenge) => {
      if (
        !scoreEquals(challenge.setScoreBeforeChallenge, setScoreBeforeChallenge)
      ) {
        dispatch({
          type: "setSetScoreBeforeChallenge",
          setScoreBeforeChallenge,
        });
      }
    };

  const setScoreAfterChallenge: SelectedChallengeValues["setSetScoreAfterChallenge"] =
    (setScoreAfterChallenge) => {
      if (
        !scoreEquals(challenge.setScoreAfterChallenge, setScoreAfterChallenge)
      ) {
        dispatch({ type: "setSetScoreAfterChallenge", setScoreAfterChallenge });
      }
    };

  const setSet: SelectedChallengeValues["setSet"] = (setNumber) => {
    if (challenge.setNumber !== setNumber) {
      dispatch({ type: "setSet", setNumber });
    }
  };

  const setTypeOfChallenge: SelectedChallengeValues["setTypeOfChallenge"] = (
    typeOfChallenge,
  ) => {
    if (challenge.challengeType !== typeOfChallenge) {
      dispatch({ type: "setTypeOfChallenge", challengeType: typeOfChallenge });
    }
  };

  const setChallenger: SelectedChallengeValues["setChallenger"] = (
    challenger,
  ) => {
    if (challenge.challenger !== challenger) {
      dispatch({ type: "setChallenger", challenger });
    }
  };

  const setChallengeResult: SelectedChallengeValues["setChallengeResult"] = (
    challengeResult,
  ) => {
    let changed = false;
    if (
      challenge.successful !== challengeResult.successful ||
      challenge.unresolved !== challengeResult.unresolved ||
      challenge.declined !== challengeResult.declined
    ) {
      changed = true;
    }
    if ("outcome" in challengeResult || "outcome" in challenge) {
      if (
        (challenge as SuccessfulChallenge).outcome !==
        (challengeResult as SuccessfulChallenge).outcome
      ) {
        changed = true;
      }
    }

    if (changed) {
      dispatch({ type: "setChallengeResult", challengeResult });
    }
  };

  const newChallenge: SelectedChallengeValues["newChallenge"] = () =>
    dispatch({ type: "newChallenge" });

  return (
    <SelectedChallengeContext.Provider
      value={{
        challenge,
        setSetScoreBeforeRally: setScoreRallyStart,
        setSetScoreBeforeChallenge: setScoreBeforeChallenge,
        setSetScoreAfterChallenge: setScoreAfterChallenge,
        setSet,
        setTypeOfChallenge,
        setChallenger,
        setChallengeResult,
        loadChallenge,
        newChallenge,
      }}
    >
      {props.children}
    </SelectedChallengeContext.Provider>
  );
};

export default SelectedChallengeContextProvider;
